import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Moon, AlertTriangle, Loader2, ChevronRight, FilePenLine, Clock, CalendarDays, List, PartyPopper, Palmtree, Hourglass } from "lucide-react";
import { CalendarioMes } from "./CalendarioMes";
import { toast } from "sonner";
import { Panel, fmtH, fmtMin, fmtHora, fmtFecha, hoyPeriodo } from "./portalShared";
import { CorreccionDialog } from "./CorreccionDialog";

const ESTADO_J = {
  cerrada: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  abierta: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  huerfana: "bg-red-500/10 text-red-300 border-red-500/30",
};

export function MisFichajes({ periodoInicial, onCambio }) {
  const [periodo, setPeriodo] = useState(periodoInicial || hoyPeriodo());
  const [vista, setVista] = useState(() => { try { return localStorage.getItem("dnf:portal:vista-fichajes") || "calendario"; } catch { return "calendario"; } });
  const [data, setData] = useState(null);
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(null); // fecha
  const [correccion, setCorreccion] = useState(null); // {fecha, fichaje}

  const cambiarVista = (v) => { setVista(v); try { localStorage.setItem("dnf:portal:vista-fichajes", v); } catch { /* ignore */ } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, c] = await Promise.all([
        api.get("/portal-empleado/jornadas", { params: { periodo } }),
        api.get("/portal-empleado/calendario", { params: { periodo } }),
      ]);
      setData(j.data); setCal(c.data);
    } catch { toast.error("Error cargando fichajes"); }
    finally { setLoading(false); }
  }, [periodo]);
  useEffect(() => { load(); }, [load]);

  const incons = data?.inconsistencias || [];
  return (
    <div className="space-y-4" data-testid="mis-fichajes">
      <div className="flex items-center gap-3 flex-wrap">
        <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="bg-zinc-900 border-zinc-800 w-44" data-testid="fichajes-mes" />
        {data && (
          <div className="text-xs text-zinc-400 flex items-center gap-3 flex-wrap">
            <span>Debía <b className="text-zinc-200 font-mono">{data.esperadas} h</b></span>
            <span>Trabajadas <b className="text-zinc-200 font-mono">{data.trabajadas} h</b></span>
            <span>Saldo <b className={`font-mono ${data.saldo > 0 ? "text-emerald-300" : data.saldo < 0 ? "text-red-300" : "text-zinc-200"}`}>{fmtH(data.saldo)}</b></span>
            {data.fuente_horario === "default" && <span className="text-amber-300 text-[10px]">horario por defecto 8 h</span>}
          </div>
        )}
        <span className="flex-1" />
        <div className="flex rounded-md overflow-hidden border border-zinc-800 h-8" role="tablist" aria-label="Vista">
          {[["calendario", CalendarDays, "Calendario"], ["lista", List, "Listado"]].map(([v, Icon, l]) => (
            <button key={v} role="tab" aria-selected={vista === v} onClick={() => cambiarVista(v)} data-testid={`vista-${v}`}
              className={`px-2.5 text-xs inline-flex items-center gap-1 ${vista === v ? "bg-zinc-100 text-zinc-900" : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
              <Icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{l}</span>
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={() => setCorreccion({ fecha: new Date().toISOString().slice(0, 10), fichaje: null })}
          className="bg-transparent border-zinc-700 text-zinc-300 h-8 text-xs" data-testid="btn-correccion-nueva">
          <FilePenLine className="w-3.5 h-3.5 mr-1" /> Olvidé fichar…
        </Button>
      </div>

      {incons.length > 0 && (
        <div className="text-xs bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 text-amber-200 flex items-start gap-2" data-testid="fichajes-anomalias">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <div>{incons.length} anomalía(s) este mes: {incons.map((i) => `${i.fecha_laboral} (${i.tipo.replace("_", " ")})`).join(", ")}. Puedes solicitar una corrección desde la jornada.</div>
        </div>
      )}

      {vista === "calendario" && (
        <Panel title="Calendario del mes" icon={CalendarDays} testid="calendario-panel">
          <div className="p-2 sm:p-3">
            {loading && !cal ? <div className="p-6 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>
              : <CalendarioMes data={cal} onDia={(f) => setDetalle(f)} />}
          </div>
        </Panel>
      )}

      {vista === "lista" && <Panel title={`Jornadas (${data?.jornadas?.length || 0})`} icon={Clock} testid="jornadas-panel">
        {loading && !data ? <div className="p-6 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>
          : !data?.jornadas?.length ? <div className="p-6 text-center text-zinc-500 text-sm" data-testid="jornadas-vacio">Sin fichajes en este mes.</div> : (
            <div className="divide-y divide-zinc-800/60">
              {data.jornadas.map((j) => (
                <button key={j.entrada_id} onClick={() => setDetalle(j.fecha_laboral)}
                  className="w-full text-left px-3 sm:px-4 py-2.5 hover:bg-zinc-900/40 flex items-center gap-2 sm:gap-3" data-testid={`jornada-${j.entrada_id}`}>
                  <div className="w-24 sm:w-28 shrink-0 font-mono text-xs text-zinc-300 capitalize">{fmtFecha(j.fecha_laboral)}</div>
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="text-zinc-100 font-mono">{fmtHora(j.inicio)}</span>
                    <span className="text-zinc-600 mx-1">→</span>
                    <span className="text-zinc-100 font-mono">{j.fin ? fmtHora(j.fin) : "…"}</span>
                    {j.cruza_medianoche && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-indigo-200 bg-indigo-500/10 border border-indigo-500/30 rounded px-1.5" data-testid={`cruza-${j.entrada_id}`}><Moon className="w-3 h-3" /> cruza medianoche</span>}
                    {j.alerta_larga && <span className="ml-2 text-[10px] text-amber-300">&gt;12 h</span>}
                    {j.correccion_pendiente && <span className="ml-2 text-[10px] text-amber-300 border border-amber-500/30 rounded px-1.5">corrección pendiente</span>}
                  </div>
                  <div className="font-mono text-sm text-zinc-200 shrink-0">{j.estado === "cerrada" ? fmtMin(j.minutos) : ""}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded border shrink-0 ${ESTADO_J[j.estado]}`}>{j.estado}</span>
                  <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                </button>
              ))}
            </div>
          )}
      </Panel>}

      <JornadaDetalleDialog fecha={detalle} onClose={() => setDetalle(null)}
        onCorregir={(fichaje) => setCorreccion({ fecha: detalle, fichaje })} />
      <CorreccionDialog open={!!correccion} fecha={correccion?.fecha} fichaje={correccion?.fichaje}
        onClose={() => setCorreccion(null)} onSaved={() => { load(); onCambio && onCambio(); }} />
    </div>
  );
}

function JornadaDetalleDialog({ fecha, onClose, onCorregir }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!fecha) { setD(null); return; }
    api.get(`/portal-empleado/jornada/${fecha}`).then((r) => setD(r.data)).catch(() => toast.error("Error cargando jornada"));
  }, [fecha]);
  return (
    <Dialog open={!!fecha} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="dialog-jornada">
        <DialogHeader>
          <DialogTitle className="capitalize">{fecha && fmtFecha(fecha)} · {fecha}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {d?.teorica && <>Jornada teórica: <b className="text-zinc-300">{fmtMin(d.teorica.minutos)}</b> ({d.teorica.fuente}{d.teorica.ausencia_tipo ? ` · ${d.teorica.ausencia_tipo}` : ""})</>}
          </DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-6 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center" data-testid="jornada-resumen-dia">
              <div className="bg-zinc-900/60 rounded p-2"><div className="text-[10px] uppercase text-zinc-500">Trabajadas</div><div className="font-mono text-zinc-100">{fmtMin(d.trabajados_min)}</div></div>
              <div className="bg-zinc-900/60 rounded p-2"><div className="text-[10px] uppercase text-zinc-500">Esperadas</div><div className="font-mono text-zinc-100">{fmtMin(d.esperados_min)}</div></div>
              <div className="bg-zinc-900/60 rounded p-2"><div className="text-[10px] uppercase text-zinc-500">Saldo día</div>
                <div className={`font-mono ${d.saldo_fiable ? (d.saldo_min > 0 ? "text-emerald-300" : d.saldo_min < 0 ? "text-red-300" : "text-zinc-100") : "text-zinc-500"}`} data-testid="jornada-saldo-dia">
                  {d.saldo_fiable ? `${d.saldo_min > 0 ? "+" : d.saldo_min < 0 ? "−" : ""}${fmtMin(Math.abs(d.saldo_min))}` : "—"}</div>
                {!d.saldo_fiable && <div className="text-[9px] text-zinc-600">no fiable (jornada abierta, anomalía o día futuro)</div>}
              </div>
            </div>
            {(d.festivo || d.ausencias?.length > 0 || d.solicitudes_horas?.length > 0) && (
              <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="jornada-contexto">
                {d.festivo && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-pink-500/10 border-pink-500/30 text-pink-300"><PartyPopper className="w-3 h-3" /> Festivo · {d.festivo.nombre}</span>}
                {d.ausencias.map((a) => (
                  <span key={a.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${a.tipo === "compensacion_horas" ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-200" : "bg-sky-500/10 border-sky-500/30 text-sky-300"}`}>
                    {a.tipo === "compensacion_horas" ? <Hourglass className="w-3 h-3" /> : <Palmtree className="w-3 h-3" />} {a.label}{a.horas ? ` · ${a.horas} h` : ""} · {a.estado}
                  </span>
                ))}
                {d.solicitudes_horas.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-amber-500/10 border-amber-500/30 text-amber-300"><Hourglass className="w-3 h-3" /> Consumo {s.horas} h · pendiente</span>
                ))}
              </div>
            )}
            {d.inconsistencias?.length > 0 && (
              <div className="text-xs text-red-300 flex items-center gap-1" data-testid="jornada-anomalias"><AlertTriangle className="w-3.5 h-3.5" /> {d.inconsistencias.map((i) => i.tipo.replace("_", " ")).join(", ")}</div>
            )}
            {d.jornadas.map((j) => (
              <div key={j.entrada_id} className="text-xs text-zinc-300 flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded border text-[10px] ${ESTADO_J[j.estado]}`}>{j.estado}</span>
                {fmtHora(j.inicio)} → {j.fin ? fmtHora(j.fin) : "sin salida"} · <b className="font-mono">{j.estado === "cerrada" ? fmtMin(j.minutos) : "—"}</b>
                {j.cruza_medianoche && <Moon className="w-3 h-3 text-indigo-200" />}
              </div>
            ))}
            <div className="border border-zinc-800 rounded-lg divide-y divide-zinc-800/60">
              {d.fichajes.map((f) => (
                <div key={f.id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`fichaje-${f.id}`}>
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${f.tipo === "entrada" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}>{f.tipo}</span>
                  <span className="font-mono text-zinc-100">{fmtHora(f.timestamp)}</span>
                  <span className="text-[10px] text-zinc-500">{f.canal || "app"}{f.edited_at ? " · editado" : ""}</span>
                  <span className="flex-1" />
                  <button onClick={() => onCorregir(f)} className="text-[11px] text-amber-300 hover:text-amber-200 underline" data-testid={`corregir-${f.id}`}>corregir</button>
                </div>
              ))}
              {d.fichajes.length === 0 && <div className="p-3 text-xs text-zinc-500">Sin fichajes.</div>}
            </div>
            <div className="flex items-center justify-between">
              <div className="text-[11px] text-zinc-500">{d.correcciones.length > 0 && `${d.correcciones.length} solicitud(es) de corrección: ${d.correcciones.map((c) => c.estado).join(", ")}`}</div>
              <Button size="sm" variant="outline" onClick={() => onCorregir(null)} className="bg-transparent border-zinc-700 text-zinc-300 h-7 text-xs" data-testid="btn-correccion-jornada">
                <FilePenLine className="w-3 h-3 mr-1" /> Olvidé fichar este día
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
